/**
 * Catalog update detection (PLAN §9).
 *
 * The catalog is precached by the service worker, which is what makes a cold
 * offline load render the real road — and is also why a running client would
 * otherwise never notice a new one. `catalog.sha256` is the escape hatch: it is
 * excluded from the precache and served `no-cache`, so fetching it is the one
 * request that always reaches the network.
 *
 * The check is deliberately cheap and quiet. It reads a 71-byte file, compares,
 * and on a difference tells the UI. It does not reload the page: a reload on a
 * long ordered list throws away the reader's position, and a catalog change is
 * never urgent — a title moved a tier, not a fire.
 *
 * Shape-breaking changes go to /catalog/v2/ instead, and v1 stays live forever
 * so old iOS builds keep working. This path therefore only ever sees content
 * changes to a schema the running client already understands.
 */

import type { Catalog } from "@schema/catalog.ts";

/** `<64-hex>  catalog.json` — the sha256sum output format. */
const SHA_LINE = /^([0-9a-f]{64})\b/;

export function parseShaFile(text: string): string | null {
  const match = SHA_LINE.exec(text.trim());
  return match?.[1] ?? null;
}

/**
 * The published hash of the *current* catalog on the server, or null when it
 * can't be established. Null is not an error worth surfacing: offline is the
 * normal case for this app, and "we couldn't check" must never be dressed up
 * as "there's an update".
 */
export async function fetchPublishedSha(signal?: AbortSignal | undefined): Promise<string | null> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}catalog/v1/catalog.sha256`, {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) return null;
    return parseShaFile(await res.text());
  } catch {
    return null;
  }
}

/**
 * Hash of the catalog this client is actually running.
 *
 * Recomputing sha256 over the loaded object is not possible — JSON.stringify
 * would have to reproduce the file's exact bytes and key order to agree with
 * the published digest, which is not a guarantee worth depending on. Instead
 * the served bytes are hashed directly. The response comes from the precache,
 * so this costs no network.
 */
export async function computeLocalSha(signal?: AbortSignal | undefined): Promise<string | null> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}catalog/v1/catalog.json`, {
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) return null;
    const bytes = await res.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

export interface UpdateCheck {
  /** A different catalog is published. Null-safe: unknown never means "yes". */
  stale: boolean;
  publishedSha: string | null;
  localSha: string | null;
}

export async function checkForCatalogUpdate(
  signal?: AbortSignal | undefined,
): Promise<UpdateCheck> {
  const [publishedSha, localSha] = await Promise.all([
    fetchPublishedSha(signal),
    computeLocalSha(signal),
  ]);
  return {
    stale: publishedSha !== null && localSha !== null && publishedSha !== localSha,
    publishedSha,
    localSha,
  };
}

/**
 * Refetch past every cache. Used only after a confirmed change, so the cost of
 * bypassing the precache is paid once and on purpose.
 *
 * The cache-busting query matters as much as `cache: "reload"`: reload only
 * defeats the *browser* cache, and a CDN holding the old response will happily
 * serve it back. Observed in the wild — a deploy went out and the site kept
 * answering with the previous catalog even on reload.
 */
export async function refetchCatalog(): Promise<Catalog> {
  const { catalogSchema } = await import("@schema/catalog.zod.ts");
  const res = await fetch(`${import.meta.env.BASE_URL}catalog/v1/catalog.json?v=${Date.now()}`, {
    cache: "reload",
  });
  if (!res.ok) throw new Error(`catalog refetch failed: HTTP ${res.status}`);
  return catalogSchema.parse(await res.json());
}
